import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryParams, useToastNotification } from '@/hooks';
import { ChangePasswordForm, MainCard } from '@/components';
import { NavigationSource, PagesPaths } from '@/enums';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { activateUser } from './userActivation';
import styles from './ActivateUser.module.scss';

const TOKEN_PARAM_KEY = 'sol';

const ActivateUser: FC = () => {
  const { t } = useTranslation('activateUser');
  const navigate = useNavigate();

  const { get: getQueryParam } = useQueryParams();
  const token = getQueryParam(TOKEN_PARAM_KEY);

  const { showToast } = useToastNotification();

  const activateUserMutation = useMutation({
    mutationFn: activateUser,
    onSuccess: () => {
      showToast({ severity: 'success', detail: t('toastSuccessText') });
      navigate(`${PagesPaths.Login}?from=${NavigationSource.UserActivation}`);
    },
    onError: (error: AxiosError) => {
      // Handle specific error cases
      if (error?.response?.status === 400) {
        showToast({ severity: 'error', detail: t('toastInvalidTokenText') });
      } else if (error?.response?.status === 401) {
        showToast({ severity: 'error', detail: t('toastExpiredTokenText') });
      } else {
        showToast({ severity: 'error', detail: t('toastErrorText') });
      }
    },
  });

  const handleFormSubmit = (newPassword: string) => {
    if (!token) {
      showToast({ severity: 'error', detail: t('toastNoTokenText') });
      return;
    }
    activateUserMutation.mutate({ password: newPassword, token });
  };

  const submitButtonText = activateUserMutation.isPending
    ? t('loadingButton')
    : t('submitButton');

  return (
    <MainCard>
      <div className={styles.activateUser}>
        <h1>{t('activateUserTitle')}</h1>
        <p>{t('passwordInstructions')}</p>
        <ChangePasswordForm
          onSubmit={handleFormSubmit}
          submitButtonText={submitButtonText}
          isLoading={activateUserMutation.isPending}
        />
      </div>
    </MainCard>
  );
};

export default ActivateUser;
